import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Opsgenie\n---\n\n## Feature Overview\n\nThe Opsgenie integration lets users forward messages sent by SELECT [Monitors](/docs/monitors/overview) (such as Spend Threshold alerts) directly to Opsgenie, allowing organizations to:\n\n* Benefit from the advanced routing capabilities from Opsgenie to dynamically forward messages to different teams and individuals, including on-call policies;\n* Use Opsgenie to acknowledge or escalate alerts based on their criticality;\n* Take advantage of Opsgenie reporting and analytics features to gain insights on how often alerts are triggered and how long it takes teams to acknowledge and resolve them.\n\nOur Opsgenie integration works by leveraging our capability to deliver Monitor alerts by email and Opsgenie's native email integration.\n\n## Step 1: Enable the email integration on Opsgenie\n\nThe first step is to enable the Opsgenie account to receive notifications by email. You can do that by following [Opsgenie's documentation](https://support.atlassian.com/opsgenie/docs/integrate-your-email-with-opsgenie/). No special settings are needed. The email integration should look similar to the one in the image below.\n\n{% blog-post-image imageName=\"opsgenieIntegrationCreated\" alt=\"Opsgenie email integration form\" imgClassName=\"rounded-md\" /%}\n\nDon't forget to enable new Opsgenie integrations by pressing the blue \"Turn on integration\" button on the page above.\n\n{% callout title=\"Friendly email addresses\" type=\"note\" %}\n\nBy default, Opsgenie will generate a new email address with a unique random ID. We recommend changing it to a more descriptive address, which will help you distinguish different email integrations in the future.\n\n{% /callout %}\n\n## Step 2: Add Opsgenie integration email address as a Monitor destination\n\nThe next step is to take the new email address that Opsgenie has created for the integration and add it as a Monitor destination here in SELECT, as seen below. More detailed instructions can be found in our [Monitors documentation](/docs/monitors).\n\n{% blog-post-image imageName=\"opsgenieDestinationCreated\" alt=\"SELECT destination created\" imgClassName=\"rounded-md\" /%}\n\n## Step 3: Set up Monitors using the new destination\n\nOnce the destination is set up on SELECT, all that is left is to set up Monitors using the new destination.\n\n{% blog-post-image imageName=\"opsgenieMonitor\" alt=\"SELECT Monitor created\" imgClassName=\"rounded-md\" /%}\n\n\n## Step 4: All done!\n\nThat's it! Now, Monitor alerts will be sent to Opsgenie and benefit from their forwarding and escalation rules, on-call policies, and schedules.\n\nOpsgenie automatically converts email messages to plain text, making the alerts sent by our Monitors look like the one below.\n\n{% blog-post-image imageName=\"opsgenieRawMessage\" alt=\"Opsgenie Raw Message\" imgClassName=\"rounded-md\" /%}\n\nHowever, you can always see the original message with our rich formatting and charts by accessing a file that Opsgenie automatically attaches to the incoming alert. To find the attachment, follow the red arrow shown in the image above.\n\n{% blog-post-image imageName=\"opsgenieFormattedMessage\" alt=\"Opsgenie Raw Message\" imgClassName=\"rounded-md\" /%}\n\nDon't hesitate to contact us if you need help setting up this or other integrations.\n";
const filepath = "/docs/opsgenie.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
